import React, { useEffect, useRef, useState } from 'react';
import { TaskViewKey } from '../../types/task.types';
import { cn } from '../../lib/cn';

const BUILT_IN: Array<{ key: TaskViewKey; label: string }> = [
  { key: 'mine', label: 'My tasks' },
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'awaiting-approval', label: 'Awaiting approval' },
  { key: 'archived', label: 'Archived' },
];

export interface TaskViewsProps {
  active: TaskViewKey;
  search: string;
  onSelectView: (view: TaskViewKey) => void;
  onSearchChange: (search: string) => void;
}

const tabClass = (on: boolean) =>
  cn(
    'mr-4 cursor-pointer pb-0.5 text-scale-1',
    on ? 'border-b-2 border-accent font-semibold text-accent' : 'text-ink-muted hover:text-ink',
  );

export const TaskViews: React.FC<TaskViewsProps> = (props) => {
  const [term, setTerm] = useState(props.search);
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastEmitted = useRef(props.search);

  useEffect(() => () => clearTimeout(debounce.current), []);

  // Sync external search changes into the input, but ignore the parent echoing
  // back a value this input just emitted so in-progress typing is never clobbered.
  useEffect(() => {
    if (props.search !== lastEmitted.current) {
      clearTimeout(debounce.current);
      setTerm(props.search);
      lastEmitted.current = props.search;
    }
  }, [props.search]);

  return (
    <div className="flex flex-wrap items-baseline justify-between gap-y-2 pt-2">
      <div className="flex flex-wrap items-baseline">
        {BUILT_IN.map((tab) => {
          const on = props.active === tab.key;
          return (
            <button key={tab.key} type="button" onClick={() => props.onSelectView(tab.key)}
              aria-current={on ? 'true' : undefined} className={tabClass(on)}>
              {tab.label}
            </button>
          );
        })}
      </div>
      <input
        value={term}
        placeholder="Search tasks…"
        onChange={(event) => {
          const { value } = event.target;
          setTerm(value);
          clearTimeout(debounce.current);
          debounce.current = setTimeout(() => {
            lastEmitted.current = value;
            props.onSearchChange(value);
          }, 300);
        }}
        className="w-44 border-b border-rule-faint bg-transparent text-scale-1 text-ink outline-none placeholder:text-ink-muted/60 focus:border-accent"
        aria-label="Search tasks"
      />
    </div>
  );
};
